// client/src/coms/components/comslayout_parts/useVoiceSession.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeChannelClientId } from './serverApi';
import { comsApiPost } from '../../comsApi';
import {
  createVoiceRoom,
  connectVoiceRoom,
  disconnectVoiceRoom,
  getLocalMicMuted,
  getLocalDeafened,
  toggleLocalMicMuted,
  toggleLocalDeafened,
  setLocalMicMuted,
  setLocalDeafened,
  applyRemoteAudioElementPrefs,
  getListenOnlyReason,
} from '../../voip/voiceClient';
import {
  MAX_VOICE_REJOIN_ATTEMPTS,
  classifyVoiceDisconnect,
  describeVoiceJoinError,
  voiceRejoinDelayMs,
} from '../../voip/voiceclient_parts/voiceReliability';
import { snapshotParticipants } from '../../voip/voiceState';
import { readMuteHotkey } from '../../settings/muteHotkeys';
import { getLocalVideoMode, getLocalVideoErr, setLocalVideoMode } from '../../voip/videoClient';
import { getParticipantPrefs } from '../../voip/participantPrefs';
import { isVoiceAudioPublication } from '../../voip/screenShareAudioManager';
import { getPersistedVoiceResume, removeComsSessionKey, setPersistedVoiceResume } from './comsSession';
import {
  buildNameByIdFromPresence,
  buildVoiceRoomsForServer,
  clamp01,
  makeCanonicalVoiceRoomName,
  normalizeDbIdKey,
  pickServerMemberDisplayName,
  safePickServer,
  toInt,
} from './layoutUtils';

const LS_VOICE_MUTED = 'vox:voiceMutedPref';
const LS_VOICE_DEAFENED = 'vox:voiceDeafenedPref';
const LS_LAST_OPEN_MAP = 'vox:lastOpenMap';

function keyCodeToKeyPart(code) {
  const c = String(code || '').trim();
  if (!c) return 'Space';
  if (c === 'Space') return 'Space';
  if (c.startsWith('Key') && c.length === 4) return c.slice(3).toUpperCase();
  if (c.startsWith('Digit') && c.length === 6) return c.slice(5);
  if (c.startsWith('Numpad')) return c;
  if (c.startsWith('F')) return c;
  if (c.startsWith('Arrow')) return c;
  if (c === 'Enter') return 'Enter';
  if (c === 'Escape') return 'Escape';
  if (c === 'Tab') return 'Tab';
  if (c === 'Backspace') return 'Backspace';
  if (c === 'Delete') return 'Delete';
  if (c === 'Insert') return 'Insert';
  if (c === 'Home') return 'Home';
  if (c === 'End') return 'End';
  if (c === 'PageUp') return 'PageUp';
  if (c === 'PageDown') return 'PageDown';
  if (c === 'CapsLock') return 'CapsLock';
  if (c === 'Pause') return 'Pause';
  if (c === 'PrintScreen') return 'PrintScreen';
  return c;
}

function buildShortcutFromKeyEvent(e) {
  const parts = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Command');
  parts.push(keyCodeToKeyPart(e.code) || 'Space');
  return parts.join('+') || 'Space';
}

function normalizeShortcutString(shortcut, fallback = '') {
  const s = String(shortcut || '').trim();
  return s || fallback;
}

function normalizeShortcutCompareValue(shortcut) {
  return String(normalizeShortcutString(shortcut, ''))
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();
}

function writeBoolLS(key, value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {}
}

function pickVoiceAnimationValue(v, fallback = 'flash') {
  const next = String(
    v && typeof v === 'object'
      ? v.voiceAnimation != null
        ? v.voiceAnimation
        : v.voice_animation != null
        ? v.voice_animation
        : v.active_voice_animation != null
        ? v.active_voice_animation
        : v.animation != null
        ? v.animation
        : fallback
      : fallback
  ).trim();
  return next || fallback;
}

function hasDesktopMuteHotkeyBridge() {
  try {
    if (typeof window === 'undefined') return false;
    return !!(
      window.__voxDesktopMuteMicShortcut ||
      window.__voxDesktopMuteSpeakerShortcut ||
      window.__voxDesktopMuteBothShortcut
    );
  } catch {
    return false;
  }
}

export default function useVoiceSession({ servers, serversLoaded, apiAvailable, server, activeServerId, setActiveServerId, navigate }) {
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceError, setVoiceError] = useState('');
  // Non-fatal information (listen-only reason, "reconnecting…").
  const [voiceNotice, setVoiceNotice] = useState('');
  // 'ok' | 'reconnecting' — LiveKit's own short reconnects; the session stays 'connected'.
  const [voiceConnection, setVoiceConnection] = useState('ok');
  const [voiceServerId, setVoiceServerId] = useState(null);
  const [voiceChannelId, setVoiceChannelId] = useState(null);
  const [voiceChannelDbId, setVoiceChannelDbId] = useState(null);
  const [voiceRoomName, setVoiceRoomName] = useState(null);
  const [voiceParticipants, setVoiceParticipants] = useState([]);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voiceDeafened, setVoiceDeafened] = useState(false);

  const [voiceShowVideo, setVoiceShowVideo] = useState(false);
  const [voiceVideoMode, setVoiceVideoMode] = useState('none');
  const [voiceVideoErr, setVoiceVideoErr] = useState('');
  const [voiceRoomObj, setVoiceRoomObj] = useState(null);

  const [voicePresenceByChannel, setVoicePresenceByChannel] = useState({});

  const voiceRoomRef = useRef(null);
  const voiceTrackRef = useRef(null);
  const voiceLeavingRef = useRef(false);
  const voiceDeafenedRef = useRef(false);
  const voiceServerIdRef = useRef(null);
  const voiceRoomUnbindRef = useRef(null);

  const presencePollStopRef = useRef(null);
  const presencePollBusyRef = useRef(false);
  // When the server reports voice as unavailable (503), poll presence rarely instead of every 3.5 s.
  const presenceUnavailableUntilRef = useRef(0);

  const voiceResumeAttemptedRef = useRef(false);
  const voiceJoinNonceRef = useRef(0);
  const voiceRejoinAttemptRef = useRef(0);
  const voiceRejoinTimerRef = useRef(null);
  const joinVoiceChannelRef = useRef(null);
  const voiceStatusRef = useRef('idle');
  const voiceMutedRef = useRef(false);
  const voiceDeafenedRef2 = useRef(false);
  const lastDesktopMuteEventRef = useRef({ key: '', ts: 0 });

  useEffect(() => {
    voiceStatusRef.current = voiceStatus;
  }, [voiceStatus]);

  useEffect(() => {
    voiceMutedRef.current = !!voiceMuted;
  }, [voiceMuted]);

  useEffect(() => {
    voiceDeafenedRef2.current = !!voiceDeafened;
  }, [voiceDeafened]);

  useEffect(() => {
    // StrictMode (development) runs this cleanup once right after mounting; without the reset the
    // "leaving" flag stayed set and every later join was silently ignored.
    voiceLeavingRef.current = false;
    return () => {
      voiceLeavingRef.current = true;
      if (voiceRejoinTimerRef.current) clearTimeout(voiceRejoinTimerRef.current);
      voiceRejoinTimerRef.current = null;
      try {
        const unbind = voiceRoomUnbindRef.current;
        voiceRoomUnbindRef.current = null;
        if (typeof unbind === 'function') unbind();
      } catch {}
      try {
        disconnectVoiceRoom(voiceRoomRef.current, voiceTrackRef.current);
      } catch {}
      voiceRoomRef.current = null;
      voiceTrackRef.current = null;
      voiceServerIdRef.current = null;
      voiceJoinNonceRef.current += 1;
      setVoiceShowVideo(false);
      setVoiceVideoMode('none');
      setVoiceVideoErr('');
      setVoiceRoomObj(null);
      try {
        const stop = presencePollStopRef.current;
        presencePollStopRef.current = null;
        if (typeof stop === 'function') stop();
      } catch {}
    };
  }, []);

  useEffect(() => {
    voiceDeafenedRef.current = !!voiceDeafened;
  }, [voiceDeafened]);

  useEffect(() => {
    voiceServerIdRef.current = voiceServerId != null ? String(voiceServerId) : null;
  }, [voiceServerId]);

  const updatePresenceFromRoom = useCallback(
    (room, channelDbId) => {
      const chKey = normalizeDbIdKey(channelDbId);
      if (!chKey) return;

      const list = snapshotParticipants(room)
        .map((p) => {
          if (!p) return null;

          const userId = String(p.identity || '').match(/(\d+)$/);
          const uid = userId && userId[1] ? userId[1] : '';
          if (!uid) return null;

          const sid = voiceServerIdRef.current || voiceServerId;
          const memberName = pickServerMemberDisplayName(servers, sid, Number(uid));
          const baseName = String(p.name || '').trim() || String(p.identity || '').trim() || `User ${uid}`;

          const audioLevelRaw =
            p.audioLevel != null
              ? p.audioLevel
              : p.audio_level != null
              ? p.audio_level
              : p.level != null
              ? p.level
              : 0;

          const audioLevel = Number(audioLevelRaw);

          const isSpeakingExplicit =
            typeof p.isSpeaking === 'boolean'
              ? p.isSpeaking
              : typeof p.is_speaking === 'boolean'
              ? p.is_speaking
              : typeof p.speaking === 'boolean'
              ? p.speaking
              : null;

          return {
            userId: Number(uid),
            name: memberName || baseName,
            muted: !!p.muted,
            deafened: !!p.deafened,
            connected: p.connected == null ? true : !!p.connected,
            isSpeaking: isSpeakingExplicit != null ? !!isSpeakingExplicit : Number.isFinite(audioLevel) && audioLevel > 0.02,
            audioLevel: Number.isFinite(audioLevel) ? audioLevel : 0,
            voiceAnimation: pickVoiceAnimationValue(p),
            voice_animation: pickVoiceAnimationValue(p),
            active_voice_animation: pickVoiceAnimationValue(p),
          };
        })
        .filter(Boolean);

      setVoicePresenceByChannel((prev) => {
        const next = { ...(prev && typeof prev === 'object' ? prev : {}) };
        next[chKey] = list;
        return next;
      });
    },
    [servers, voiceServerId]
  );

  const syncRoomSnapshot = useCallback(
    (room, channelDbId) => {
      try {
        setVoiceParticipants(snapshotParticipants(room));
      } catch {}
      try {
        updatePresenceFromRoom(room, channelDbId);
      } catch {}
    },
    [updatePresenceFromRoom]
  );

  const ensurePresenceTimer = useCallback(
    (room, channelDbId) => {
      if (!room) return () => {};
      let alive = true;

      const tick = async () => {
        if (!alive) return;
        try {
          updatePresenceFromRoom(room, channelDbId);
        } catch {}
      };

      const i = setInterval(tick, 1200);
      tick();

      return () => {
        alive = false;
        clearInterval(i);
      };
    },
    [updatePresenceFromRoom]
  );

  const applyRemoteAudioPrefs = useCallback(
    (room, uid, kind) => {
      try {
        if (!room) return;

        const serverIdForPrefs = String(voiceServerIdRef.current || voiceServerId || '').trim();

        const participants = [];
        try {
          if (room.remoteParticipants && typeof room.remoteParticipants.values === 'function') {
            for (const p of room.remoteParticipants.values()) participants.push(p);
          }
        } catch {}

        for (const p of participants) {
          if (!p) continue;

          const rawIdentity = String(p.identity || '');
          const m = rawIdentity.match(/(\d+)$/);
          const puid = m && m[1] ? Number(m[1]) : NaN;
          if (!Number.isFinite(puid)) continue;
          if (uid != null && Number(uid) !== puid) continue;

          const participantName = String(p.name || '').trim() || rawIdentity || `User ${puid}`;

          const pref = getParticipantPrefs({
            serverId: serverIdForPrefs,
            participantId: String(puid),
            participantName,
          });

          const volPct = Number(pref && pref.volume != null ? pref.volume : 100);
          const vol = clamp01(volPct / 100);
          const muted = !!(pref && pref.muted);

          const audioTracks = [];
          try {
            const pubs = p.audioTrackPublications ? Array.from(p.audioTrackPublications.values()) : [];
            for (const pub of pubs) {
              if (!pub) continue;
              if (kind && kind !== 'audio') continue;
              if (!isVoiceAudioPublication(p, pub)) continue;
              const tr = pub.track || null;
              if (!tr) continue;
              audioTracks.push(tr);
            }
          } catch {}

          for (const tr of audioTracks) {
            try {
              if (typeof tr.setVolume === 'function') {
                tr.setVolume(muted ? 0 : vol);
              }
            } catch {}
          }
        }

        try {
          applyRemoteAudioElementPrefs(room, { serverId: serverIdForPrefs });
        } catch {}
      } catch {}
    },
    [voiceServerId]
  );

  const bindRoomEvents = useCallback(
    (room, channelDbId) => {
      if (!room) return () => {};

      let unPresence = () => {};
      try {
        unPresence = ensurePresenceTimer(room, channelDbId);
      } catch {}

      function syncAll() {
        if (voiceRoomRef.current !== room) return;
        syncRoomSnapshot(room, channelDbId);
      }

      function onParticipantConnected() {
        syncAll();
        if (voiceRoomRef.current !== room) return;
        applyRemoteAudioPrefs(room, null, null);
      }

      function onParticipantDisconnected() {
        syncAll();
      }

      function onTrackSubscribed() {
        syncAll();
        if (voiceRoomRef.current !== room) return;
        applyRemoteAudioPrefs(room, null, null);
      }

      function onTrackUnsubscribed() {
        syncAll();
      }

      try {
        room.on('participantConnected', onParticipantConnected);
        room.on('participantDisconnected', onParticipantDisconnected);
        room.on('trackSubscribed', onTrackSubscribed);
        room.on('trackUnsubscribed', onTrackUnsubscribed);
      } catch {}

      syncAll();
      if (voiceRoomRef.current === room) applyRemoteAudioPrefs(room, null, null);

      return () => {
        try {
          room.off('participantConnected', onParticipantConnected);
          room.off('participantDisconnected', onParticipantDisconnected);
          room.off('trackSubscribed', onTrackSubscribed);
          room.off('trackUnsubscribed', onTrackUnsubscribed);
        } catch {}
        try {
          unPresence();
        } catch {}
      };
    },
    [ensurePresenceTimer, syncRoomSnapshot, applyRemoteAudioPrefs]
  );

  const fetchVoiceTokenDirect = useCallback(async (room) => {
    const payload = await comsApiPost('/voice/token', { room });
    const ok = payload && payload.ok;
    const token = payload && payload.token ? String(payload.token) : '';
    const url = payload && payload.url ? String(payload.url) : '';
    if (!ok || !token || !url) {
      const detail = payload && payload.error ? String(payload.error) : 'Failed to get voice token';
      throw new Error(detail);
    }
    return { token, url, permissions: payload && payload.permissions ? payload.permissions : null };
  }, []);

  const pollVoicePresenceForServer = useCallback(
    async (s) => {
      if (!s) return;
      if (presencePollBusyRef.current) return;
      if (Date.now() < presenceUnavailableUntilRef.current) return;
      presencePollBusyRef.current = true;

      try {
        const rooms = buildVoiceRoomsForServer(s);
        if (!rooms.length) return;

        const resp = await comsApiPost('/voice/presence', {
          rooms: rooms.map((r) => r.room),
        });

        if (!resp || resp.ok !== true) return;

        const presence = resp.presence && typeof resp.presence === 'object' ? resp.presence : {};
        const nextByChannel = {};

        for (const r of rooms) {
          const arr = Array.isArray(presence[r.room]) ? presence[r.room] : [];
          const cleaned = arr
            .map((p) => {
              if (!p) return null;
              const rawId = p.userId != null ? String(p.userId) : p.identity != null ? String(p.identity) : '';
              const m = rawId.match(/(\d+)$/);
              const uid = m && m[1] ? Number(m[1]) : NaN;
              if (!Number.isFinite(uid)) return null;

              const memberName = pickServerMemberDisplayName(servers, s.id, uid);
              const name = memberName || (String(p.name || '').trim() || `User ${uid}`);

              const audioLevelRaw =
                p.audioLevel != null
                  ? p.audioLevel
                  : p.audio_level != null
                  ? p.audio_level
                  : p.level != null
                  ? p.level
                  : 0;

              const audioLevel = Number(audioLevelRaw);

              const isSpeakingExplicit =
                typeof p.isSpeaking === 'boolean'
                  ? p.isSpeaking
                  : typeof p.is_speaking === 'boolean'
                  ? p.is_speaking
                  : typeof p.speaking === 'boolean'
                  ? p.speaking
                  : null;

              return {
                userId: uid,
                name,
                muted: !!p.muted,
                deafened: !!p.deafened,
                connected: p.connected == null ? true : !!p.connected,
                isSpeaking: isSpeakingExplicit != null ? !!isSpeakingExplicit : Number.isFinite(audioLevel) && audioLevel > 0.02,
                audioLevel: Number.isFinite(audioLevel) ? audioLevel : 0,
                voiceAnimation:
                  String(
                    p.voiceAnimation != null
                      ? p.voiceAnimation
                      : p.voice_animation != null
                      ? p.voice_animation
                      : 'flash'
                  ).trim() || 'flash',
              };
            })
            .filter(Boolean);

          nextByChannel[normalizeDbIdKey(r.channelDbId)] = cleaned;
        }

        setVoicePresenceByChannel((prev) => {
          const base = { ...(prev && typeof prev === 'object' ? prev : {}) };
          for (const k of Object.keys(nextByChannel)) base[k] = nextByChannel[k];
          return base;
        });
      } catch (e) {
        if (Number(e && e.status) === 503) presenceUnavailableUntilRef.current = Date.now() + 60000;
      } finally {
        presencePollBusyRef.current = false;
      }
    },
    [servers]
  );

  useEffect(() => {
    try {
      const stop = presencePollStopRef.current;
      presencePollStopRef.current = null;
      if (typeof stop === 'function') stop();
    } catch {}

    if (!server) return;
    if (!apiAvailable) return;
    if (!serversLoaded) return;

    let alive = true;

    const tick = async () => {
      if (!alive) return;
      if (voiceStatus === 'connected') return;
      await pollVoicePresenceForServer(server);
    };

    const i = setInterval(tick, 3500);
    tick();

    const stop = () => {
      alive = false;
      clearInterval(i);
    };

    presencePollStopRef.current = stop;
    return stop;
  }, [server, apiAvailable, serversLoaded, voiceStatus, pollVoicePresenceForServer]);

  useEffect(() => {
    if (voiceStatus !== 'connected') return;
    if (!voiceRoomObj) return;

    const reapply = () => {
      try {
        const sid = String(voiceServerIdRef.current || voiceServerId || '').trim();
        applyRemoteAudioElementPrefs(voiceRoomObj, { serverId: sid });
      } catch {}
    };

    reapply();

    window.addEventListener('pa-vtt-proximity-changed', reapply);
    return () => window.removeEventListener('pa-vtt-proximity-changed', reapply);
  }, [voiceStatus, voiceRoomObj, voiceServerId]);

  const joinVoiceChannel = useCallback(
    async (serverId, channelId, channelDbId, joinOpts = {}) => {
      const isRejoin = !!(joinOpts && joinOpts.isRejoin);
      let createdRoom = null;
      let joinNonce = 0;
      try {
        const leaving = voiceLeavingRef.current;
        if (leaving) return;

        if (voiceRejoinTimerRef.current) {
          clearTimeout(voiceRejoinTimerRef.current);
          voiceRejoinTimerRef.current = null;
        }
        if (!isRejoin) voiceRejoinAttemptRef.current = 0;

        try {
          const currentPath = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
          if (currentPath && /\/maps?(\/|$)/i.test(currentPath)) {
            window.localStorage.setItem(LS_LAST_OPEN_MAP, currentPath);
          }
        } catch {}

        joinNonce = voiceJoinNonceRef.current + 1;
        voiceJoinNonceRef.current = joinNonce;

        try {
          const unbindPrev = voiceRoomUnbindRef.current;
          voiceRoomUnbindRef.current = null;
          if (typeof unbindPrev === 'function') unbindPrev();
        } catch {}

        try {
          disconnectVoiceRoom(voiceRoomRef.current, voiceTrackRef.current);
        } catch {}
        voiceRoomRef.current = null;
        voiceTrackRef.current = null;

        const sid = String(serverId);
        const cid = String(channelId);
        const cdb = String(channelDbId);

        const serverObj = safePickServer(servers, sid);
        const serverDbId = serverObj && serverObj.db_id != null ? toInt(serverObj.db_id, 0) : 0;
        const canonicalRoom = makeCanonicalVoiceRoomName(serverDbId, cdb);
        if (!canonicalRoom) throw new Error('Invalid voice room');

        setVoiceStatus(isRejoin ? 'reconnecting' : 'joining');
        setVoiceError('');
        if (!isRejoin) setVoiceNotice('');
        setVoiceConnection('ok');
        setVoiceServerId(sid);
        setVoiceChannelId(cid);
        setVoiceChannelDbId(cdb);
        setVoiceRoomName(canonicalRoom);

        const tokenData = await fetchVoiceTokenDirect(canonicalRoom);

        const room = createVoiceRoom({
          onParticipantsChanged: (r) => {
            if (voiceJoinNonceRef.current !== joinNonce) return;
            if (voiceRoomRef.current && voiceRoomRef.current !== r) return;
            syncRoomSnapshot(r, cdb);
          },
          onReconnecting: () => {
            if (voiceJoinNonceRef.current !== joinNonce) return;
            setVoiceConnection('reconnecting');
          },
          onReconnected: () => {
            if (voiceJoinNonceRef.current !== joinNonce) return;
            setVoiceConnection('ok');
          },
          onDisconnected: (_r, reason) => {
            if (voiceJoinNonceRef.current !== joinNonce) return;
            if (voiceRoomRef.current && voiceRoomRef.current !== room) return;

            // The room is gone: release the microphone, gate pipeline and remote audio elements.
            try {
              const unbind = voiceRoomUnbindRef.current;
              voiceRoomUnbindRef.current = null;
              if (typeof unbind === 'function') unbind();
            } catch {}
            try {
              disconnectVoiceRoom(room, voiceTrackRef.current);
            } catch {}
            voiceRoomRef.current = null;
            voiceTrackRef.current = null;
            setVoiceRoomObj(null);
            setVoiceConnection('ok');

            const verdict = classifyVoiceDisconnect(reason);
            const canRejoin =
              verdict.rejoin && !voiceLeavingRef.current && voiceRejoinAttemptRef.current < MAX_VOICE_REJOIN_ATTEMPTS;

            if (canRejoin) {
              const attempt = voiceRejoinAttemptRef.current++;
              setVoiceStatus('reconnecting');
              setVoiceNotice(verdict.message);
              voiceRejoinTimerRef.current = setTimeout(() => {
                voiceRejoinTimerRef.current = null;
                const join = joinVoiceChannelRef.current;
                if (typeof join === 'function') join(sid, cid, cdb, { isRejoin: true });
              }, voiceRejoinDelayMs(attempt));
            } else if (verdict.message) {
              setVoiceStatus('error');
              setVoiceError(verdict.rejoin ? 'Voice connection lost. Join the channel again to reconnect.' : verdict.message);
              setVoiceNotice('');
            } else {
              setVoiceStatus('idle');
              setVoiceNotice('');
              setVoiceServerId(null);
              setVoiceChannelId(null);
              setVoiceChannelDbId(null);
              setVoiceRoomName(null);
            }
            try {
              setVoiceParticipants([]);
            } catch {}
            try {
              const chKey = normalizeDbIdKey(cdb);
              if (chKey) {
                setVoicePresenceByChannel((prev) => {
                  const next = { ...(prev && typeof prev === 'object' ? prev : {}) };
                  next[chKey] = [];
                  return next;
                });
              }
            } catch {}
            try {
              setVoiceMuted(!!getLocalMicMuted());
            } catch {
              setVoiceMuted(false);
            }
            try {
              setVoiceDeafened(!!getLocalDeafened());
            } catch {
              setVoiceDeafened(false);
            }
          },
        });
        createdRoom = room;

        await connectVoiceRoom({ room, url: tokenData.url, token: tokenData.token, permissions: tokenData.permissions });
        if (voiceJoinNonceRef.current !== joinNonce) {
          try {
            disconnectVoiceRoom(room, null);
          } catch {}
          return () => {};
        }
        voiceRoomRef.current = room;
        setVoiceRoomObj(room);

        try {
          setVoiceMuted(!!getLocalMicMuted());
        } catch {
          setVoiceMuted(false);
        }
        try {
          setVoiceDeafened(!!getLocalDeafened());
        } catch {
          setVoiceDeafened(false);
        }

        try {
          applyRemoteAudioElementPrefs(room, { serverId: sid });
        } catch {}

        const unbind = bindRoomEvents(room, cdb);
        voiceRoomUnbindRef.current = unbind;

        setVoiceStatus('connected');
        voiceRejoinAttemptRef.current = 0;
        setVoiceNotice(getListenOnlyReason());
        syncRoomSnapshot(room, cdb);

        return () => {
          try {
            if (voiceRoomUnbindRef.current === unbind) voiceRoomUnbindRef.current = null;
          } catch {}
          try {
            unbind();
          } catch {}
        };
      } catch (e) {
        // Never leave a connected-but-untracked room behind (it would show the user as in the channel).
        if (createdRoom && voiceRoomRef.current !== createdRoom) {
          try {
            disconnectVoiceRoom(createdRoom, null);
          } catch {}
        }
        if (joinNonce && voiceJoinNonceRef.current !== joinNonce) return;

        const status = Number(e && e.status);
        const transient = !status || status >= 500;
        if (isRejoin && transient && status !== 503 && voiceRejoinAttemptRef.current < MAX_VOICE_REJOIN_ATTEMPTS) {
          const attempt = voiceRejoinAttemptRef.current++;
          const sid = String(serverId);
          const cid = String(channelId);
          const cdb = String(channelDbId);
          setVoiceStatus('reconnecting');
          voiceRejoinTimerRef.current = setTimeout(() => {
            voiceRejoinTimerRef.current = null;
            const join = joinVoiceChannelRef.current;
            if (typeof join === 'function') join(sid, cid, cdb, { isRejoin: true });
          }, voiceRejoinDelayMs(attempt));
          return;
        }

        setVoiceStatus('error');
        setVoiceNotice('');
        setVoiceError(describeVoiceJoinError(e));
      }
    },
    [bindRoomEvents, fetchVoiceTokenDirect, servers, syncRoomSnapshot]
  );

  useEffect(() => {
    joinVoiceChannelRef.current = joinVoiceChannel;
  }, [joinVoiceChannel]);

  // A moderator moved us (server-sent 'vox-moderation' packet): switch to that voice channel. The server
  // removes us from the old room shortly after, so this is a courtesy, not the enforcement.
  useEffect(() => {
    const onMove = (e) => {
      const d = (e && e.detail) || {};
      const dbId = Number(d.to_channel_id) || 0;
      const sid = voiceServerIdRef.current;
      if (!dbId || !sid || typeof joinVoiceChannelRef.current !== 'function') return;
      joinVoiceChannelRef.current(sid, makeChannelClientId('voice', dbId), dbId).catch?.(() => {});
    };
    window.addEventListener('vox-voice-move', onMove);
    return () => window.removeEventListener('vox-voice-move', onMove);
  }, []);

  // Microphone allowed after joining listen-only.
  useEffect(() => {
    const onReady = () => setVoiceNotice(getListenOnlyReason());
    window.addEventListener('vox-voice-mic-ready', onReady);
    return () => window.removeEventListener('vox-voice-mic-ready', onReady);
  }, []);

  const leaveVoiceChannel = useCallback(async () => {
    const prevChannelDbId = voiceChannelDbId != null ? String(voiceChannelDbId) : '';
    try {
      voiceLeavingRef.current = true;
      voiceJoinNonceRef.current += 1;
      if (voiceRejoinTimerRef.current) clearTimeout(voiceRejoinTimerRef.current);
      voiceRejoinTimerRef.current = null;
      voiceRejoinAttemptRef.current = 0;

      setVoiceStatus('idle');
      setVoiceError('');
      setVoiceNotice('');
      setVoiceConnection('ok');
      setVoiceServerId(null);
      setVoiceChannelId(null);
      setVoiceChannelDbId(null);
      setVoiceRoomName(null);
      setVoiceParticipants([]);

      try {
        setLocalMicMuted(false);
      } catch {}
      try {
        setLocalDeafened(false);
      } catch {}
      setVoiceMuted(false);
      setVoiceDeafened(false);
      writeBoolLS(LS_VOICE_MUTED, false);
      writeBoolLS(LS_VOICE_DEAFENED, false);

      try {
        const unbind = voiceRoomUnbindRef.current;
        voiceRoomUnbindRef.current = null;
        if (typeof unbind === 'function') unbind();
      } catch {}

      try {
        const chKey = normalizeDbIdKey(prevChannelDbId);
        if (chKey) {
          setVoicePresenceByChannel((prev) => {
            const next = { ...(prev && typeof prev === 'object' ? prev : {}) };
            next[chKey] = [];
            return next;
          });
        }
      } catch {}

      try {
        disconnectVoiceRoom(voiceRoomRef.current, voiceTrackRef.current);
      } catch {}

      voiceRoomRef.current = null;
      voiceTrackRef.current = null;
      voiceServerIdRef.current = null;
      setVoiceRoomObj(null);

      setVoiceShowVideo(false);
      setVoiceVideoMode('none');
      setVoiceVideoErr('');
    } finally {
      voiceLeavingRef.current = false;
    }
  }, [voiceChannelDbId]);

  const toggleVoiceVideo = useCallback(() => {
    const current = String(voiceVideoMode || 'none');
    const next = current === 'none' ? 'camera' : 'none';
    (async () => {
      try {
        const room = voiceRoomRef.current;
        if (!room) return;

        setVoiceVideoErr('');

        const cur = getLocalVideoMode(room) || 'none';
        if (cur === next) {
          setVoiceVideoMode(next);
          setVoiceShowVideo(next !== 'none');
          return;
        }

        await setLocalVideoMode(room, next);

        const err = getLocalVideoErr(room) || '';
        if (err) setVoiceVideoErr(err);

        setVoiceVideoMode(next);
        setVoiceShowVideo(next !== 'none');
      } catch (e) {
        setVoiceVideoErr(String(e?.message || e || 'Video toggle failed'));
      }
    })();
  }, [voiceVideoMode]);

  const toggleVoiceCamera = useCallback(() => {
    const current = String(voiceVideoMode || 'none');
    const hasCam = current === 'camera' || current === 'both';
    const hasScreen = current === 'screen' || current === 'both';
    const next = hasCam ? (hasScreen ? 'screen' : 'none') : hasScreen ? 'both' : 'camera';

    (async () => {
      try {
        const room = voiceRoomRef.current;
        if (!room) return;

        setVoiceVideoErr('');

        const cur = getLocalVideoMode(room) || 'none';
        if (cur === next) {
          setVoiceVideoMode(next);
          setVoiceShowVideo(next !== 'none');
          return;
        }

        await setLocalVideoMode(room, next);

        const err = getLocalVideoErr(room) || '';
        if (err) setVoiceVideoErr(err);

        setVoiceVideoMode(next);
        setVoiceShowVideo(next !== 'none');
      } catch (e) {
        setVoiceVideoErr(String(e?.message || e || 'Video toggle failed'));
      }
    })();
  }, [voiceVideoMode]);

  const toggleVoiceScreen = useCallback(() => {
    const current = String(voiceVideoMode || 'none');
    const hasCam = current === 'camera' || current === 'both';
    const hasScreen = current === 'screen' || current === 'both';
    const next = hasScreen ? (hasCam ? 'camera' : 'none') : hasCam ? 'both' : 'screen';

    (async () => {
      try {
        const room = voiceRoomRef.current;
        if (!room) return;

        setVoiceVideoErr('');

        const cur = getLocalVideoMode(room) || 'none';
        if (cur === next) {
          setVoiceVideoMode(next);
          setVoiceShowVideo(next !== 'none');
          return;
        }

        await setLocalVideoMode(room, next);

        const err = getLocalVideoErr(room) || '';
        if (err) setVoiceVideoErr(err);

        setVoiceVideoMode(next);
        setVoiceShowVideo(next !== 'none');
      } catch (e) {
        setVoiceVideoErr(String(e?.message || e || 'Video toggle failed'));
      }
    })();
  }, [voiceVideoMode]);

  useEffect(() => {
    function onPrefsChanged(e) {
      const room = voiceRoomRef.current;
      if (!room) return;

      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
      const sid = String(detail.serverId != null ? detail.serverId : voiceServerIdRef.current || voiceServerId || '').trim();
      const pid = String(detail.participantId != null ? detail.participantId : '').trim();

      applyRemoteAudioPrefs(room, pid || null, null);

      try {
        applyRemoteAudioElementPrefs(room, { serverId: sid, participantId: pid || '' });
      } catch {}
    }

    window.addEventListener('pa-coms-voice-participant-prefs-changed', onPrefsChanged);
    return () => window.removeEventListener('pa-coms-voice-participant-prefs-changed', onPrefsChanged);
  }, [applyRemoteAudioPrefs, voiceServerId]);

  const applyMuteHotkey = useCallback(() => {
    try {
      const next = !getLocalMicMuted();
      setLocalMicMuted(next);
      setVoiceMuted(next);
      writeBoolLS(LS_VOICE_MUTED, next);
      return next;
    } catch {
      setVoiceMuted((prev) => {
        const next = !prev;
        writeBoolLS(LS_VOICE_MUTED, next);
        return next;
      });
      return null;
    }
  }, []);

  const applyDeafenHotkey = useCallback(() => {
    try {
      const next = !getLocalDeafened();
      setLocalDeafened(next);
      setVoiceDeafened(next);
      writeBoolLS(LS_VOICE_DEAFENED, next);
      return next;
    } catch {
      setVoiceDeafened((prev) => {
        const next = !prev;
        writeBoolLS(LS_VOICE_DEAFENED, next);
        return next;
      });
      return null;
    }
  }, []);

  const applyBothHotkey = useCallback(() => {
    try {
      const currentMuted = !!getLocalMicMuted();
      const currentDeafened = !!getLocalDeafened();
      const next = !(currentMuted && currentDeafened);
      setLocalMicMuted(next);
      setLocalDeafened(next);
      setVoiceMuted(next);
      setVoiceDeafened(next);
      writeBoolLS(LS_VOICE_MUTED, next);
      writeBoolLS(LS_VOICE_DEAFENED, next);
      return next;
    } catch {
      const next = !(voiceMutedRef.current && voiceDeafenedRef2.current);
      setVoiceMuted(next);
      setVoiceDeafened(next);
      writeBoolLS(LS_VOICE_MUTED, next);
      writeBoolLS(LS_VOICE_DEAFENED, next);
      return next;
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e) return;
      if (e.__voxShortcutCaptureHandled) return;
      if (e.defaultPrevented) return;
      if (e.repeat) return;
      if (voiceStatusRef.current !== 'connected') return;

      if (hasDesktopMuteHotkeyBridge()) return;

      const pressed = normalizeShortcutCompareValue(buildShortcutFromKeyEvent(e));
      if (!pressed) return;

      const muteMicShortcut = normalizeShortcutCompareValue(readMuteHotkey('mic'));
      const muteSpeakerShortcut = normalizeShortcutCompareValue(readMuteHotkey('speaker'));
      const muteBothShortcut = normalizeShortcutCompareValue(readMuteHotkey('both'));

      if (muteBothShortcut && pressed === muteBothShortcut) {
        applyBothHotkey();
        return;
      }

      if (muteMicShortcut && pressed === muteMicShortcut) {
        applyMuteHotkey();
        return;
      }

      if (muteSpeakerShortcut && pressed === muteSpeakerShortcut) {
        applyDeafenHotkey();
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyMuteHotkey, applyDeafenHotkey, applyBothHotkey]);

  useEffect(() => {
    const shouldIgnoreDuplicate = (kind, detail) => {
      const ts = Number(detail && detail.ts ? detail.ts : 0);
      const key = `${kind}:${String(detail && detail.shortcut ? detail.shortcut : '')}`;
      const prev = lastDesktopMuteEventRef.current || { key: '', ts: 0 };

      if (prev.key === key && ts > 0 && prev.ts > 0 && Math.abs(ts - prev.ts) < 250) {
        return true;
      }

      lastDesktopMuteEventRef.current = { key, ts: ts || Date.now() };
      return false;
    };

    const onDesktopMuteMic = (e) => {
      if (voiceStatusRef.current !== 'connected') return;
      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
      if (shouldIgnoreDuplicate('mic', detail)) return;
      applyMuteHotkey();
    };

    const onDesktopMuteSpeaker = (e) => {
      if (voiceStatusRef.current !== 'connected') return;
      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
      if (shouldIgnoreDuplicate('speaker', detail)) return;
      applyDeafenHotkey();
    };

    const onDesktopMuteBoth = (e) => {
      if (voiceStatusRef.current !== 'connected') return;
      const detail = e && e.detail && typeof e.detail === 'object' ? e.detail : {};
      if (shouldIgnoreDuplicate('both', detail)) return;
      applyBothHotkey();
    };

    window.addEventListener('coms-global-mute-mic', onDesktopMuteMic);
    window.addEventListener('coms-global-mute-speaker', onDesktopMuteSpeaker);
    window.addEventListener('coms-global-mute-both', onDesktopMuteBoth);

    return () => {
      window.removeEventListener('coms-global-mute-mic', onDesktopMuteMic);
      window.removeEventListener('coms-global-mute-speaker', onDesktopMuteSpeaker);
      window.removeEventListener('coms-global-mute-both', onDesktopMuteBoth);
    };
  }, [applyMuteHotkey, applyDeafenHotkey, applyBothHotkey]);

  const voiceSession = useMemo(
    () => ({
      status: voiceStatus,
      error: voiceError,
      notice: voiceNotice,
      connection: voiceConnection,
      serverId: voiceServerId,
      channelId: voiceChannelId,
      channelDbId: voiceChannelDbId,
      roomName: voiceRoomName,
      participants: voiceParticipants,
      muted: voiceMuted,
      deafened: voiceDeafened,
      showVideo: voiceShowVideo,
      videoMode: voiceVideoMode,
      videoErr: voiceVideoErr,
      presenceByChannel: voicePresenceByChannel,
    }),
    [
      voiceStatus,
      voiceError,
      voiceNotice,
      voiceConnection,
      voiceServerId,
      voiceChannelId,
      voiceChannelDbId,
      voiceRoomName,
      voiceParticipants,
      voiceMuted,
      voiceDeafened,
      voiceShowVideo,
      voiceVideoMode,
      voiceVideoErr,
      voicePresenceByChannel,
    ]
  );

  useEffect(() => {
    const connectedLike = voiceStatus === 'connected' || voiceStatus === 'joining' || voiceStatus === 'reconnecting';
    setPersistedVoiceResume({
      wasConnected: connectedLike,
      serverId: voiceServerId,
      channelId: voiceChannelId,
      channelDbId: voiceChannelDbId,
    });
  }, [voiceStatus, voiceServerId, voiceChannelId, voiceChannelDbId]);

  useEffect(() => {
    if (voiceResumeAttemptedRef.current) return;
    if (!serversLoaded) return;
    if (!Array.isArray(servers) || !servers.length) {
      voiceResumeAttemptedRef.current = true;
      return;
    }
    if (voiceStatus === 'joining' || voiceStatus === 'connected') {
      voiceResumeAttemptedRef.current = true;
      return;
    }

    const saved = getPersistedVoiceResume();
    if (!saved) {
      voiceResumeAttemptedRef.current = true;
      return;
    }

    const s = servers.find((x) => String(x.id) === String(saved.serverId));
    if (!s) {
      removeComsSessionKey('lastVoiceSession');
      voiceResumeAttemptedRef.current = true;
      return;
    }

    const ch = (Array.isArray(s.channels) ? s.channels : []).find(
      (c) =>
        String(c.id) === String(saved.channelId) &&
        String(c.kind || '') === 'voice' &&
        String(c.db_id) === String(saved.channelDbId)
    );
    if (!ch) {
      removeComsSessionKey('lastVoiceSession');
      voiceResumeAttemptedRef.current = true;
      return;
    }

    if (String(activeServerId || '') !== String(s.id)) {
      setActiveServerId(String(s.id));
      navigate(`/comms/${String(s.id)}`, { replace: true });
    }

    voiceResumeAttemptedRef.current = true;
    joinVoiceChannel(String(s.id), String(ch.id), String(ch.db_id));
  }, [serversLoaded, servers, voiceStatus, activeServerId, navigate, joinVoiceChannel, setActiveServerId]);

  const chatNameById = useMemo(() => buildNameByIdFromPresence(voicePresenceByChannel), [voicePresenceByChannel]);

  const onToggleVoiceMute = useCallback(() => {
    try {
      const next = !!toggleLocalMicMuted();
      setVoiceMuted(next);
      writeBoolLS(LS_VOICE_MUTED, next);
    } catch {
      setVoiceMuted((v) => {
        const next = !v;
        writeBoolLS(LS_VOICE_MUTED, next);
        return next;
      });
    }
  }, []);

  const onToggleVoiceDeafen = useCallback(() => {
    try {
      const next = !!toggleLocalDeafened();
      setVoiceDeafened(next);
      writeBoolLS(LS_VOICE_DEAFENED, next);
    } catch {
      setVoiceDeafened((v) => {
        const next = !v;
        writeBoolLS(LS_VOICE_DEAFENED, next);
        return next;
      });
    }
  }, []);

  return {
    voiceSession,
    voiceRoomObj,
    voiceShowVideo,
    voiceVideoMode,
    voiceVideoErr,
    joinVoiceChannel,
    leaveVoiceChannel,
    toggleVoiceVideo,
    toggleVoiceCamera,
    toggleVoiceScreen,
    onToggleVoiceMute,
    onToggleVoiceDeafen,
    chatNameById,
  };
}